/**
 * Menu의 ARIA `menu` 키보드 내비게이션 순수 헬퍼 — roving 포커스/타입어헤드.
 *
 * React 상태에 의존하지 않고 DOM 항목 배열만 다룬다(`Menu` 컨테이너에서 매번
 * `[role="menuitem"]`을 질의해 넘긴다). 비활성(`aria-disabled`) 항목도 포함해 포커스
 * 가능하게 둔다 — caption 사유를 키보드로 읽게 하려는 의도(활성화 차단은 항목 쪽 책임).
 */
import type { KeyboardEvent as ReactKeyboardEvent } from "react";

export interface MenuTypeaheadState {
  buffer: string;
  timer: number;
}

/** `from`에서 `delta`만큼(보통 ±1) 이동하며 끝에서 wrap. */
export function focusRelative(items: readonly HTMLElement[], from: number, delta: number): void {
  const count = items.length;
  if (count === 0) {
    return;
  }
  // from === -1(비항목에서 진입)이어도 안전하게 wrap.
  const next = (((from + delta) % count) + count) % count;
  items[next]?.focus();
}

/**
 * 입력 글자를 버퍼에 누적(500ms 후 초기화)하고, 현재 항목 다음부터 한 바퀴 돌며
 * 라벨 prefix가 매칭되는 첫 항목으로 포커스를 옮긴다. `textContent`는 라벨로 시작하므로
 * caption이 뒤에 붙어도 prefix 매칭은 영향받지 않는다.
 */
export function focusByTypeahead(
  state: MenuTypeaheadState,
  items: readonly HTMLElement[],
  from: number,
  key: string,
): void {
  window.clearTimeout(state.timer);
  state.buffer += key.toLowerCase();
  state.timer = window.setTimeout(() => {
    state.buffer = "";
  }, 500);

  const buffer = state.buffer;
  const count = items.length;
  for (let offset = 1; offset <= count; offset += 1) {
    const candidate = items[(from + offset) % count];
    const text = (candidate?.textContent ?? "").trim().toLowerCase();
    if (candidate && text.startsWith(buffer)) {
      candidate.focus();
      return;
    }
  }
}

/** 타입어헤드 대상이 되는 단일 인쇄 가능 글자(수정키 조합 제외). */
export function isPrintableKey(event: ReactKeyboardEvent): boolean {
  return event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
}
